import type { IntentClassification, IntentContext } from './types';

const informationalKeywords = ['learn', 'understand', 'what', 'guide', 'how', 'explain'];
const navigationalKeywords = ['go to', 'open', 'switch', 'navigate', 'jump'];
const commercialKeywords = ['compare', 'pricing', 'value', 'best', 'plan'];
const transactionalKeywords = ['apply', 'start now', 'submit', 'pay', 'book', 'ready'];

export function classifyIntent(context: IntentContext): IntentClassification {
  const query = context.query.toLowerCase();
  const signalTypes = context.signals.map((signal) => signal.type);

  if (signalTypes.includes('ready-to-execute') || signalTypes.includes('submit')) {
    return {
      intent: 'TRANSACTIONAL',
      confidence: 0.94,
      reasoning: [
        'The user emitted an execution-ready signal.',
        'Recent behavior indicates intent to mutate state rather than continue reading.',
      ],
      supportingSignals: context.signals.filter((signal) =>
        signal.type === 'ready-to-execute' || signal.type === 'submit'
      ),
    };
  }

  if (commercialKeywords.some((keyword) => query.includes(keyword))) {
    return {
      intent: 'COMMERCIAL',
      confidence: 0.86,
      reasoning: [
        'The query includes comparison/value language.',
        'Commercial decision-support blocks are likely more useful than direct action UI.',
      ],
      supportingSignals: context.signals,
    };
  }

  if (navigationalKeywords.some((keyword) => query.includes(keyword))) {
    return {
      intent: 'NAVIGATIONAL',
      confidence: 0.88,
      reasoning: [
        'The query emphasizes movement between destinations or workspaces.',
        'A shortcut-oriented layout is more appropriate than long-form content.',
      ],
      supportingSignals: context.signals,
    };
  }

  if (informationalKeywords.some((keyword) => query.includes(keyword)) || query.length > 0) {
    return {
      intent: 'INFORMATIONAL',
      confidence: 0.84,
      reasoning: [
        'The query reads like a knowledge-seeking prompt.',
        'The user has not yet emitted a strong execution signal.',
      ],
      supportingSignals: context.signals,
    };
  }

  return {
    intent: 'NAVIGATIONAL',
    confidence: 0.42,
    reasoning: [
      'No meaningful query was provided.',
      'The engine cannot confidently compose a specialized interface.',
    ],
    supportingSignals: context.signals,
  };
}